// @ts-nocheck
import { trapFocusContainer } from '@theme/utilities';
import { dispatchWishlistRefresh } from '@theme/events';

const dialogs = new Map();
const openDialogs = [];

const lockState = {
  count: 0,
  previousOverflow: '',
};

const lockBody = () => {
  lockState.count += 1;

  if (lockState.count === 1) {
    lockState.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('dialog-open');
  }
};

const unlockBody = () => {
  lockState.count = Math.max(0, lockState.count - 1);

  if (lockState.count === 0) {
    document.body.style.overflow = lockState.previousOverflow;
    lockState.previousOverflow = '';
    document.body.classList.remove('dialog-open');
  }
};

const removeOpenDialog = (dialogContent) => {
  const index = openDialogs.indexOf(dialogContent);
  if (index >= 0) {
    openDialogs.splice(index, 1);
  }
};

const getTopOpenDialog = () => openDialogs[openDialogs.length - 1] || null;

const blurMouseRestoredProductCardFocus = () => {
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    !active.matches(':focus-visible') &&
    active.closest('.product-card')
  ) {
    active.blur();
  }
};

class DialogTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.updateAria();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
  }

  static get observedAttributes() {
    return ['id', 'data-dialog-target'];
  }

  attributeChangedCallback() {
    this.updateAria();
  }

  getDialogTargetId() {
    return this.dataset.dialogTarget || this.getAttribute('id');
  }

  updateAria() {
    const targetId = this.getDialogTargetId();
    if (targetId) {
      this.setAttribute('aria-controls', targetId);
      this.setAttribute('aria-haspopup', 'dialog');
    }
  }

  onClick = (event) => {
    const targetId = this.getDialogTargetId();
    if (!targetId) {
      return;
    }

    const interactiveElement = event.target.closest(
      "button, a, [role='button'], input, select, textarea, label"
    );

    if (!interactiveElement && event.target === this) {
      return;
    }

    event.preventDefault();
    window.dialog?.open(targetId);
  };

  onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const targetId = this.getDialogTargetId();
    if (!targetId) {
      return;
    }

    event.preventDefault();
    window.dialog?.open(targetId);
  };
}

class DialogContent extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.isOpen = false;
    this.dialogElement = null;
    this.restoreFocus = null;
    this.closeAnimationCleanup = null;
    /** @type {{ hide: () => void, destroy: () => void } | null} */
    this.backdropCursor = null;
  }

  connectedCallback() {
    this.initialize();
    if (this.id) {
      dialogs.set(this.id, this);
    }
  }

  disconnectedCallback() {
    if (dialogs.get(this.id) === this) {
      dialogs.delete(this.id);
    }

    if (this.dialogElement) {
      this.dialogElement.removeEventListener('click', this.handleDialogClick);
      this.dialogElement.removeEventListener(
        'keydown',
        this.handleDialogKeydown
      );
      this.dialogElement.removeEventListener('close', this.handleNativeClose);
      this.destroyBackdropCursor();
      if (this.dialogElement.parentNode) {
        this.dialogElement.parentNode.removeChild(this.dialogElement);
      }
      this.dialogElement = null;
    }

    if (this.isOpen) {
      removeOpenDialog(this);
      unlockBody();
      this.isOpen = false;
    }
  }

  static get observedAttributes() {
    return ['id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'id') {
      return;
    }

    if (oldValue && dialogs.get(oldValue) === this) {
      dialogs.delete(oldValue);
    }

    if (newValue) {
      dialogs.set(newValue, this);
      if (this.dialogElement) {
        this.dialogElement.setAttribute('data-dialog-id', newValue);
      }
    }
  }

  initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.hidden = true;

    const dialogElement = document.createElement('dialog');
    dialogElement.className = 'theme-dialog';
    if (this.id) {
      dialogElement.setAttribute('data-dialog-id', this.id);
    }

    dialogElement.addEventListener('click', this.handleDialogClick);
    dialogElement.addEventListener('keydown', this.handleDialogKeydown);
    dialogElement.addEventListener('close', this.handleNativeClose);

    this.dialogElement = dialogElement;
    document.body.appendChild(dialogElement);
  }

  getDialogNodes() {
    const template = this.querySelector('template');
    if (template instanceof HTMLTemplateElement) {
      return template.content.cloneNode(true);
    }

    const fragment = document.createDocumentFragment();
    Array.from(this.childNodes).forEach((node) => {
      fragment.appendChild(node.cloneNode(true));
    });
    return fragment;
  }

  ensureCloseButton() {
    if (!this.dialogElement) {
      return;
    }

    if (this.dialogElement.querySelector('[data-dialog-close]')) {
      return;
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'overlay-close dialog-close';
    closeButton.setAttribute('data-dialog-close', '');
    closeButton.setAttribute('aria-label', 'Close dialog');
    closeButton.textContent = '×';
    this.dialogElement.prepend(closeButton);
  }

  getDialogContentRoot() {
    if (!this.dialogElement) {
      return null;
    }

    const panel = this.dialogElement.querySelector(
      '.quick-view-template, [data-dialog-panel]'
    );

    if (panel instanceof HTMLElement) {
      return panel;
    }

    const firstChild = this.dialogElement.firstElementChild;
    return firstChild instanceof HTMLElement ? firstChild : null;
  }

  initBackdropCursor() {
    this.destroyBackdropCursor();

    if (
      typeof window.initBackdropCursor !== 'function' ||
      !this.dialogElement
    ) {
      return;
    }

    const contentRoot = this.getDialogContentRoot();

    // Backdrop cross — do not wire from block/section scripts.
    this.backdropCursor = window.initBackdropCursor({
      root: this.dialogElement,
      contentRoot: contentRoot ?? undefined,
      isEnabled: () => this.isOpen,
    });
  }

  destroyBackdropCursor() {
    this.backdropCursor?.destroy();
    this.backdropCursor = null;
  }

  applyDialogClass() {
    if (!this.dialogElement) {
      return;
    }

    const previousClass = this.dialogElement.dataset.dynamicDialogClass;
    if (previousClass) {
      this.dialogElement.classList.remove(previousClass);
      delete this.dialogElement.dataset.dynamicDialogClass;
    }

    const nextClass = this.getAttribute('data-dialog-class');
    if (nextClass) {
      this.dialogElement.classList.add(nextClass);
      this.dialogElement.dataset.dynamicDialogClass = nextClass;
    }
  }

  open = () => {
    if (!this.dialogElement || this.isOpen) {
      return;
    }

    if (this.closeAnimationCleanup) {
      this.closeAnimationCleanup();
    }

    while (this.dialogElement.firstChild) {
      this.dialogElement.removeChild(this.dialogElement.firstChild);
    }

    const nodes = this.getDialogNodes();
    this.dialogElement.appendChild(nodes);
    this.ensureCloseButton();
    this.applyDialogClass();
    this.initBackdropCursor();

    this.isOpen = true;
    this.restoreFocus = trapFocusContainer(this.dialogElement);

    this.dialogElement.setAttribute('scroll-lock', '');
    this.dialogElement.style.opacity = '0';
    this.dialogElement.classList.remove('hiding');
    this.dialogElement.classList.add('showing');

    lockBody();

    removeOpenDialog(this);
    openDialogs.push(this);

    const onShowEnd = (event) => {
      if (event.animationName !== 'dialog-show') {
        return;
      }

      this.dialogElement.classList.remove('showing');
      this.dialogElement.style.opacity = '';
      this.dialogElement.removeEventListener('animationend', onShowEnd);
    };

    this.dialogElement.addEventListener('animationend', onShowEnd);
    this.dialogElement.showModal();

    this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    dispatchWishlistRefresh();
  };

  close = () => {
    if (
      !this.dialogElement ||
      !this.isOpen ||
      this.dialogElement.classList.contains('hiding')
    ) {
      return;
    }

    this.isOpen = false;
    this.backdropCursor?.hide();
    this.dialogElement.removeAttribute('scroll-lock');
    this.dialogElement.classList.remove('showing');
    this.dialogElement.classList.add('hiding');

    removeOpenDialog(this);
    unlockBody();

    const finish = (event) => {
      if (event.animationName !== 'dialog-hide') {
        return;
      }

      this.dialogElement.removeEventListener('animationend', finish);
      this.dialogElement.style.opacity = '';

      if (this.dialogElement.open) {
        this.dialogElement.close();
      }

      if (typeof this.restoreFocus === 'function') {
        this.restoreFocus();
        this.restoreFocus = null;
      }

      blurMouseRestoredProductCardFocus();

      this.dialogElement.classList.remove('hiding');
      this.closeAnimationCleanup = null;
      this.destroyBackdropCursor();
    };

    this.dialogElement.addEventListener('animationend', finish);

    this.closeAnimationCleanup = () => {
      this.dialogElement.removeEventListener('animationend', finish);
      this.dialogElement.classList.remove('hiding');
      if (this.dialogElement.open) {
        this.dialogElement.close();
      }
      if (typeof this.restoreFocus === 'function') {
        this.restoreFocus();
        this.restoreFocus = null;
      }

      blurMouseRestoredProductCardFocus();
      this.closeAnimationCleanup = null;
      this.destroyBackdropCursor();
    };

    window.setTimeout(() => {
      if (this.closeAnimationCleanup) {
        this.closeAnimationCleanup();
      }
    }, 320);

    this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
  };

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  handleDialogClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (
      event.target === this.dialogElement ||
      event.target.closest('[data-dialog-close]') ||
      event.target.classList.contains('dialog-close') ||
      event.target.classList.contains('overlay-close')
    ) {
      event.preventDefault();
      this.close();
    }
  };

  handleDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  handleNativeClose = () => {
    if (this.isOpen) {
      this.close();
    }
  };
}

if (!customElements.get('dialog-trigger')) {
  customElements.define('dialog-trigger', DialogTrigger);
}

if (!customElements.get('dialog-content')) {
  customElements.define('dialog-content', DialogContent);
}

window.dialog = {
  open(id) {
    dialogs.get(id)?.open();
  },
  close(id) {
    if (id) {
      dialogs.get(id)?.close();
      return;
    }

    getTopOpenDialog()?.close();
  },
  toggle(id) {
    dialogs.get(id)?.toggle();
  },
};

export function openDialog(contentOrId) {
  if (typeof contentOrId === 'string') {
    window.dialog?.open(contentOrId);
    return;
  }

  const fallbackId = 'dialog-runtime';
  let runtimeDialog = dialogs.get(fallbackId);

  if (!runtimeDialog) {
    runtimeDialog = document.createElement('dialog-content');
    runtimeDialog.id = fallbackId;
    document.body.appendChild(runtimeDialog);
  }

  if (!(runtimeDialog instanceof DialogContent)) {
    return;
  }

  while (runtimeDialog.firstChild) {
    runtimeDialog.removeChild(runtimeDialog.firstChild);
  }

  if (contentOrId instanceof DocumentFragment) {
    runtimeDialog.appendChild(contentOrId.cloneNode(true));
  } else if (contentOrId instanceof Node) {
    runtimeDialog.appendChild(contentOrId.cloneNode(true));
  } else if (typeof contentOrId === 'string') {
    runtimeDialog.innerHTML = contentOrId;
  }

  runtimeDialog.open();
}

export function closeDialog(id) {
  window.dialog?.close(id);
}

if (typeof window !== 'undefined') {
  window.openDialog = openDialog;
  window.closeDialog = closeDialog;
}
